import "@/class-component-hooks";
import { Vue, Component, Ref, Watch } from "vue-property-decorator";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_BUTTON_SETTINGS_SAVE, COLOR_BUTTON_SETTINGS_EDIT } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import router from "@/router/router";
import { Route } from "vue-router";
import { setUpdateHold } from "@/utils/UpdateHold";
import { isViewAsSelected } from "@/store/modules/TargetUserModule";

/** One settings page is open at a time, so a single shared hold id is enough. */
const UPDATE_HOLD_ID = "settings-page-unsaved-edits";

/**
 * Base mixin for settings pages that follow the view/edit mode pattern.
 *
 * Provides:
 * - `isEditMode` / `readonly` state management
 * - `onBack()` with unsaved-changes confirmation dialog
 * - `leavePage()`, where saving and going back lead (history back unless overridden)
 * - `onSave()` that toggles edit mode or delegates to `doSave()`
 * - `buttonColor` / `backButtonLabel` computed
 * - Toolbar back button guard via `beforeRouteLeave`
 * - Toolbar title setup via `setToolbarTitle()`
 *
 * Subclasses must:
 * - Define `get hasChanged(): boolean`
 * - Implement `doSave(): Promise<void>` (actual persistence logic)
 * - Call `setToolbarTitle()` in `mounted()`
 * - Include `<confirmation-dialog ref="confirm" />` in template
 */
@Component
export default class SettingsPageMixin extends Vue {
  isEditMode = false;
  isLoading = false;
  private allowNavigation = false;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  get readonly() {
    return !this.isEditMode;
  }

  /** Override in subclass to hide the edit/save button when the user lacks permission. */
  get canEdit(): boolean {
    return true;
  }

  /**
   * True while "Sicht auf" shows another user: settings are read-only then (ticket 2610051). Pages
   * hide their edit button on it beside `canEdit`, which most of them override without `super`.
   */
  get isViewAsReadOnly(): boolean {
    return isViewAsSelected();
  }

  get backButtonLabel() {
    return this.isEditMode && this.hasChanged ? "common.abort" : "common.back";
  }

  /** SICHERN green / BEARBEITEN yellow; the dark palette's shades in dark mode. */
  get buttonColor() {
    return themedFill(this.isEditMode
      ? COLOR_BUTTON_SETTINGS_SAVE
      : COLOR_BUTTON_SETTINGS_EDIT);
  }

  /** The label of that button: white in light mode, as clients have always seen it; black on the dark palette's shades. */
  get buttonTextClass() {
    return themedFillTextClass(this.isEditMode
      ? COLOR_BUTTON_SETTINGS_SAVE
      : COLOR_BUTTON_SETTINGS_EDIT, "white--text");
  }

  /** Override in subclass — return true when form has unsaved edits. */
  get hasChanged(): boolean {
    return false;
  }

  /**
   * True while a reload would throw away the user's edits.
   *
   * The "new version" banner is held back for as long as this is true, and appears on its own once
   * the page is saved, discarded or left. See `@/utils/UpdateHold`.
   */
  get holdsAppUpdate(): boolean {
    return this.isEditMode && this.hasChanged;
  }

  @Watch("holdsAppUpdate")
  onHoldsAppUpdateChanged(holdsAppUpdate: boolean) {
    setUpdateHold(UPDATE_HOLD_ID, holdsAppUpdate);
  }

  beforeDestroy() {
    // A page torn down mid-edit (route change, sign-out) must not leave the offer suppressed.
    setUpdateHold(UPDATE_HOLD_ID, false);
  }

  /** Override in subclass — persist settings and return. Navigation is handled by the mixin. */
  async doSave(): Promise<void> {
    // no-op — must be overridden
  }

  async onSave() {
    if (this.isViewAsReadOnly) {
      return;
    }

    if (!this.isEditMode) {
      this.isEditMode = true;
      return;
    }

    if (!this.hasChanged) {
      return;
    }

    this.isLoading = true;
    try {
      await this.doSave();
    } catch {
      this.isLoading = false;
      return;
    }

    SnackbarModule.showSuccess({ key: "common.changesSaved" });
    this.isLoading = false;
    this.allowNavigation = true;
    this.leavePage();
  }

  async onBack() {
    if (this.isEditMode && this.hasChanged) {
      const confirmed = await this.showUnsavedChangesDialog();
      if (!confirmed) return;
    }

    this.allowNavigation = true;
    this.leavePage();
  }

  /**
   * Where SICHERN and ZURÜCK / ABBRECHEN go: back in history. Override for a page that must reach a
   * fixed parent even when opened directly (a reload, a shared link), where history may lead elsewhere.
   */
  leavePage() {
    router.back();
  }

  /** Intercepts toolbar back button and browser navigation. */
  async beforeRouteLeave(_to: Route, _from: Route, next: Function) {
    if (this.allowNavigation || !this.isEditMode || !this.hasChanged) {
      next();
      return;
    }

    const confirmed = await this.showUnsavedChangesDialog();
    if (confirmed) {
      this.allowNavigation = true;
      next();
    } else {
      next(false);
    }
  }

  private async showUnsavedChangesDialog(): Promise<boolean> {
    return this.confirmationDialogComponent.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      rejectMessage: this.$t("common.back").toString(),
      acceptMessage: this.$t("common.abort").toString(),
    });
  }

  setToolbarTitle(pageTitle: string) {
    const baseTitle = this.$t("pages.settings").toString();
    const toolbarTitle = baseTitle + " > " + pageTitle;
    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
